import React from "react";
import { Alert, Box, Button, Chip, Collapse, LinearProgress, Typography } from "@mui/material";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import { useOfflineSync } from "../../providers/OfflineSyncProvider";
import { MALAWI_BRAND } from "../../config/brand";

/**
 * Thin status bar for teachers — offline mode, pending uploads, manual sync.
 */
export default function OfflineSyncBar() {
  const {
    isOnline,
    pendingCount,
    syncing,
    lastSyncError,
    runSync,
  } = useOfflineSync();

  const showBar = !isOnline || pendingCount > 0 || lastSyncError;

  return (
    <Collapse in={showBar}>
      <Box sx={{ mb: 2 }}>
        {syncing && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

        {!isOnline && (
          <Alert
            severity="warning"
            icon={<CloudOffOutlinedIcon />}
            sx={{ borderRadius: 2, alignItems: "center" }}
          >
            <Typography variant="body2" fontWeight={600}>
              Offline mode — {MALAWI_BRAND.tagline}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary">
              Attendance and marks are saved on this device and will upload when you have signal.
            </Typography>
          </Alert>
        )}

        {isOnline && pendingCount > 0 && (
          <Alert
            severity="info"
            icon={<SyncOutlinedIcon />}
            sx={{ borderRadius: 2, alignItems: "center" }}
            action={
              <Button color="inherit" size="small" disabled={syncing} onClick={() => runSync()}>
                Sync now
              </Button>
            }
          >
            <StackRow
              label={`${pendingCount} item${pendingCount === 1 ? "" : "s"} waiting to upload`}
              chip={syncing ? "Syncing…" : "Pending"}
            />
          </Alert>
        )}

        {isOnline && pendingCount === 0 && lastSyncError && (
          <Alert
            severity="error"
            sx={{ borderRadius: 2, mt: isOnline ? 1 : 0 }}
            action={
              <Button color="inherit" size="small" onClick={() => runSync()}>
                Retry
              </Button>
            }
          >
            {lastSyncError}
          </Alert>
        )}

        {isOnline && pendingCount === 0 && !lastSyncError && showBar === false && (
          <Alert severity="success" icon={<CloudDoneOutlinedIcon />} sx={{ display: "none" }} />
        )}
      </Box>
    </Collapse>
  );
}

function StackRow({ label, chip }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
      <Typography variant="body2" fontWeight={600}>
        {label}
      </Typography>
      <Chip size="small" label={chip} color="primary" variant="outlined" />
    </Box>
  );
}
